/**
 * Compare-mode state for the SMSWiser stats tabs: a toggle + a second (Range B) date
 * window + a chart granularity, so each row can show Range A | Range B | Δ and a trend
 * chart can bucket by day/week/month. In-memory (not URL-synced) for v1. Range B defaults
 * to the period immediately preceding Range A.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useState } from 'react';

import { addDaysToIso } from '@/features/smswiser-campaigns/utils/dateFormat';
import {
  buildComparePreset,
  type ComparePresetCode,
} from '@/features/dashboard/compare/state/comparePresets';
import type { Granularity } from '@/features/dashboard/compare/data/types';

/** The equal-length window immediately before [from,to] (ISO YYYY-MM-DD). */
export function previousPeriodIso(from: string, to: string): { from: string; to: string } {
  if (!from || !to) return { from, to };
  const a = new Date(`${from}T12:00:00.000Z`).getTime();
  const b = new Date(`${to}T12:00:00.000Z`).getTime();
  const lenDays = Math.max(1, Math.round((b - a) / 86_400_000) + 1);
  const cTo = addDaysToIso(from, -1);
  return { from: addDaysToIso(cTo, -(lenDays - 1)), to: cTo };
}

export interface StatsCompareState {
  compareMode: boolean;
  compareFrom: string;
  compareTo: string;
  granularity: Granularity;
  toggleCompareMode: () => void;
  setCompareRange: (from: string, to: string) => void;
  setCompareFrom: (v: string) => void;
  setCompareTo: (v: string) => void;
  setGranularity: (g: Granularity) => void;
  /** Apply a dual-range preset: A (current) = the main range, B (baseline) = the compare range. */
  applyPreset: (code: ComparePresetCode) => void;
}

export function useStatsCompareState(
  primaryFrom: string,
  primaryTo: string,
  setMainRange?: (from: string, to: string) => void,
): StatsCompareState {
  const [compareMode, setCompareMode] = useState(false);
  const [range, setRange] = useState(() => previousPeriodIso(primaryFrom, primaryTo));
  const [granularity, setGranularity] = useState<Granularity>('day');

  const toggleCompareMode = useCallback(() => {
    setCompareMode((on) => {
      if (!on) setRange(previousPeriodIso(primaryFrom, primaryTo));
      return !on;
    });
  }, [primaryFrom, primaryTo]);

  const setCompareRange = useCallback(
    (from: string, to: string) => setRange({ from, to }),
    [],
  );
  const setCompareFrom = useCallback(
    (v: string) => setRange((r) => ({ ...r, from: v })),
    [],
  );
  const setCompareTo = useCallback(
    (v: string) => setRange((r) => ({ ...r, to: v })),
    [],
  );

  const applyPreset = useCallback(
    (code: ComparePresetCode) => {
      const built = buildComparePreset(code);
      if (!built) return;
      setRange(built.a); // B = baseline (older)
      setMainRange?.(built.b.from, built.b.to); // A = current (newer) = the main range
    },
    [setMainRange],
  );

  return {
    compareMode,
    compareFrom: range.from,
    compareTo: range.to,
    granularity,
    toggleCompareMode,
    setCompareRange,
    setCompareFrom,
    setCompareTo,
    setGranularity,
    applyPreset,
  };
}
